import {httpServiceV2} from './httpServiceV2'
import {EnhancementReportStatsResponse, EnhancementStatsRequest} from '../models/enhancement/enhancement-report-stats'
import {OnTimeSubmissionReportResponse} from '../models/enhancement/on-time-submission-report'
import {DataLastUpdatedResponse} from '../models/enhancement/data-last-updated-response'
import {PayPeriodResponse, PayPeriodData} from '../models/enhancement/pay-period-data'

const reportBaseURL = 'v2/Reporting'

const appendArrayParam = (params: URLSearchParams, paramName: string, values: number | number[] | null | undefined) => {
  if (!values) return
  const valueArray = Array.isArray(values) ? values : [values]
  if (valueArray.length > 0 && !valueArray.includes(0)) {
    valueArray.forEach(id => {
      if (id) {
        params.append(paramName, id.toString())
      }
    })
  }
}

const buildQueryParams = (request: EnhancementStatsRequest, includeDatesAsEmpty = false): URLSearchParams => {
  const params = new URLSearchParams()
  appendArrayParam(params, 'DepartmentId', request.departmentId)
  appendArrayParam(params, 'TeamId', request.teamId)

  if (includeDatesAsEmpty || request.dateFrom) {
    params.append('DateFrom', request.dateFrom?.toString() ?? '')
  }
  if (includeDatesAsEmpty || request.dateTo) {
    params.append('DateTo', request.dateTo?.toString() ?? '')
  }

  // Add request types parameter for filtering
  if (request.requestTypesCsv) {
    params.append('RequestTypesCsv', request.requestTypesCsv)
  }

  return params
}

const makeReportRequest = async <T>(endpoint: string, params: URLSearchParams): Promise<T> => {
  try {
    const queryString = params.toString()
    const urlSuffix = queryString ? `?${queryString}` : ''
    const url = `${reportBaseURL}/${endpoint}${urlSuffix}`
    return await httpServiceV2.get<T>(url)
  } catch (error) {
    console.error(`Error fetching ${endpoint} with params ${params.toString()}:`, error)
    throw new Error(`Failed to fetch ${endpoint} report`)
  }
}

export const enhancementReportService = {
  getEnhancementStats: async (
    request: EnhancementStatsRequest = {}
  ): Promise<EnhancementReportStatsResponse> => {
    const params = buildQueryParams(request, true)
    return makeReportRequest<EnhancementReportStatsResponse>('EnhancementStats', params)
  },

  getOnTimeSubmissionReport: async (
    request: EnhancementStatsRequest = {}
  ): Promise<OnTimeSubmissionReportResponse> => {
    const params = buildQueryParams(request, false)
    return makeReportRequest<OnTimeSubmissionReportResponse>('EnhancementOnTimeSubmissionReport', params)
  },

  getDataLastUpdated: async (): Promise<DataLastUpdatedResponse> =>
    httpServiceV2.get<DataLastUpdatedResponse>(`${reportBaseURL}/DataLastUpdated`),

  getPayPeriods: async (): Promise<PayPeriodResponse> => {
    try {
      return await httpServiceV2.get<PayPeriodResponse>(`${reportBaseURL}/PayPeriods`)
    } catch (error) {
      console.error('Error fetching pay periods:', error)
      throw new Error('Failed to fetch pay periods')
    }
  },

  getEarliestPayPeriod: async (): Promise<PayPeriodData | null> => {
    try {
      return await httpServiceV2.get<PayPeriodData>(`${reportBaseURL}/EarliestEnhancementPayPeriod`)
    } catch (error) {
      console.error('Error fetching earliest pay period:', error)
      throw new Error('Failed to fetch earliest pay period')
    }
  }
}